import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Popover, Divider, CircularProgress } from '@mui/material';
import { useRecoilValue, useSetRecoilState, useRecoilState, useRecoilCallback } from 'recoil';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import { atom_routeToStopLocation, atom_tourState, atom_isNavigationMode, atom_showNavigationBubble } from '@app/shared/state/tour.atom';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { createPortal } from 'react-dom';
import WazeMapsIcon from '@app/shared/components/wazeMaps.svg';
import GoogleMapsIcon from '@app/shared/components/googleMaps.jpeg';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';
import AppleMapsIcon from '@app/shared/components/appleMaps.jpeg';
import { useTranslation } from 'react-i18next';
import { fetchTransitRoute, TravelMode } from '@app/shared/services/transit.service';
import { routeToStopLocation } from '@app/shared/models/tour.type';
import { isIOS } from '@app/shared/utils/env';
import { IOSLocationHelpInline } from '@app/shared/components/IOSLocationHelpInline';
import { Z_INDEX } from '@app/shared/styles/zIndex';

const NavigationHelperContent: React.FC = () => {
    const { t } = useTranslation();
    const activeStop = useRecoilValue(atom_activeStopState);
    const openedStop = useRecoilValue(atom_openedStopState);
    const setOpenedStop = useSetRecoilState(atom_openedStopState);
    const setShowModal = useSetRecoilState(atom_showModalContent);
    const setModalType = useSetRecoilState(atom_modalType);
    const navState = useRecoilValue(atom_navigationHelperState);
    const setNavState = useSetRecoilState(atom_navigationHelperState);
    const tourState = useRecoilValue(atom_tourState);
    const [state_routeToStopLocation, setState_RouteToStopLocation] = useRecoilState(atom_routeToStopLocation);
    const setIsNavigationMode = useSetRecoilState(atom_isNavigationMode);
    const setShowNavigationBubble = useSetRecoilState(atom_showNavigationBubble);
    const [isLoadingTransit, setIsLoadingTransit] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [selectedMode, setSelectedMode] = useState<TravelMode | null>(null);
    const [showOtherApps, setShowOtherApps] = useState(false);

    const { anchorEl, offset, usePopover = false, reverseOrder = false } = navState;
    const open = Boolean(anchorEl);
    const [isClosing, setIsClosing] = useState(false);
    const [clickingButton, setClickingButton] = useState<string | null>(null);

    // Store the anchorEl reference when opening to ensure we can return focus
    const anchorElRef = useRef<HTMLElement | null>(null);

    // Track drag/swipe to close
    const startPosRef = useRef<{ x: number; y: number } | null>(null);

    useEffect(() => {
        if (open && anchorEl) {
            anchorElRef.current = anchorEl as HTMLElement;
        }
    }, [open, anchorEl]);

    const onClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setNavState({ anchorEl: null, offset: undefined, usePopover: false, reverseOrder: false });
            setIsClosing(false);
            setLocalError(null); // Clear error after popup closes
            setSelectedMode(null); // Reset selected mode
            setShowOtherApps(false); // Reset other-apps expansion

            // Return focus after state update
            setTimeout(() => {
                if (anchorElRef.current) {
                    anchorElRef.current.focus();
                }
            }, 0);
        }, 200); // Match the transition duration
    };
    const boxRef = useRef<HTMLDivElement>(null);
    const [calculatedPosition, setCalculatedPosition] = useState<{ top: number; left: number; transform: string }>({
        top: 0,
        left: 0,
        transform: `translate(-50%, calc(-100% - ${offset?.top || 10}px))`
    });

    const latitude = activeStop?.location?.latitude || activeStop?.stop_location?.latitude;
    const longitude = activeStop?.location?.longitude || activeStop?.stop_location?.longitude;

    // Calculate position - use offsets directly from props or calculate from anchor
    useEffect(() => {
        if (!anchorEl || !open || !boxRef.current) return;

        const anchorRect = anchorEl.getBoundingClientRect();
        const boxRect = boxRef.current.getBoundingClientRect();

        // Calculate base position: above anchor, centered
        const baseTop = anchorRect.top - boxRect.height;
        const baseLeft = anchorRect.left + anchorRect.width / 2;
        // Apply offsets from props
        const top = baseTop + (offset?.top || 0);
        const left = baseLeft + (offset?.x || 0);

        setCalculatedPosition({
            top,
            left,
            transform: 'translateX(-50%)'
        });
    }, [anchorEl, open, offset]);

    // Transit data fetching function
    const getTransitData = useRecoilCallback(({ snapshot }) => async (mode: TravelMode = 'transit') => {
        setIsLoadingTransit(true);
        setLocalError(null); // Clear any previous error

        // Read geolocation from atom only when this function is called
        const geolocation = await snapshot.getPromise(atom_geolocationState);

        // Check if geolocation is available from atom
        if (!geolocation.latitude || !geolocation.longitude) {
            let errorMessage = t('directions.location_failed');
            if (geolocation.error) {
                if (geolocation.error.code === 1) {
                    errorMessage = t('directions.location_permission_denied');
                } else if (geolocation.error.code === 2) {
                    errorMessage = t('directions.location_unavailable');
                } else if (geolocation.error.code === 3) {
                    errorMessage = t('directions.location_timeout');
                }
            }
            console.error(errorMessage);
            setLocalError(errorMessage);
            setIsLoadingTransit(false);
            return; // Don't close - show error in popup
        }

        try {
            const { latitude: userLat, longitude: userLng } = geolocation;
            const language = tourState?.language || 'en';
            console.log('[ROUTE-DEBUG] NavigationHelperContent fetching route', {
                activeStopId: activeStop?.id,
                activeStopTitle: activeStop?.s_title,
                openedStopId: openedStop?.id,
                openedStopTitle: openedStop?.s_title,
                sameStop: activeStop?.id === openedStop?.id,
                destLat: activeStop?.stop_location?.latitude,
                destLng: activeStop?.stop_location?.longitude,
                userLat,
                userLng,
                mode,
            });
            const transitData = await fetchTransitRoute(
                userLat,
                userLng,
                activeStop?.stop_location?.latitude ?? 0,
                activeStop?.stop_location?.longitude ?? 0,
                language,
                mode
            );
            console.log('[ROUTE-DEBUG] fetchTransitRoute returned', {
                hasData: !!transitData,
                routeCount: transitData?.routes?.length ?? (transitData ? 1 : 0),
            });
            if (transitData) {
                // Include stopImage and stopTitle from activeStop
                const stopImage = activeStop?.loc_pics?.[0]?.url;
                const stopTitle = activeStop?.s_title;

                // Handle both formats: routes array (new) or single route (old)
                const routes = transitData.routes || [transitData];
                if (routes.length > 0) {
                    const firstRoute = routes[0];
                    setState_RouteToStopLocation({
                        ...firstRoute,
                        stopImage,
                        stopTitle,
                        allRoutes: routes.length > 1 ? routes as any : undefined,
                        selectedRouteIndex: routes.length > 1 ? 0 : undefined,
                        travelMode: mode,
                        tourId: tourState?.id,
                    } as routeToStopLocation);
                    // Match MobileNavigationContent: dismiss any open stop modal/BigStopPage
                    // so the user lands on the map and sees the freshly-fetched route.
                    setShowModal(false);
                    setModalType(null);
                    setOpenedStop(undefined);
                    setIsNavigationMode(true);
                    setShowNavigationBubble(true); // Show the navigate pill in MapFilters from the moment navigation starts
                    setIsLoadingTransit(false);
                    passiveCollector.trackNavigationUsed('in_app_' + mode);
                    onClose(); // Only close on success
                } else {
                    setLocalError(t('directions.transit_fetch_failed') || 'No route was found');
                    setIsLoadingTransit(false);
                }
            } else {
                setLocalError(t('directions.transit_fetch_failed') || 'No route was found');
                setIsLoadingTransit(false);
                // Don't close - show error in popup
            }
        } catch (error: any) {
            console.error('Error fetching transit data:', error);
            const failedMode = (error as any)?.mode as string | undefined;
            const isNoRoute = typeof error?.message === 'string' && error.message.startsWith('NO_ROUTE_FOR_MODE:');
            let errorMessage: string;
            if (isNoRoute && failedMode) {
                const modeLabels: Record<string, string> = {
                    transit: t('directions.mode_transit') || 'transit',
                    walking: t('directions.mode_walking') || 'walking',
                    bicycling: t('directions.mode_bicycling') || 'bicycling',
                    driving: t('directions.mode_driving') || 'driving',
                };
                const modeLabel = modeLabels[failedMode] || failedMode;
                errorMessage = (t('directions.no_route_for_mode', { mode: modeLabel }) as string)
                    || `No ${modeLabel} route is available between these points.`;
            } else {
                errorMessage = error instanceof Error ? error.message : (t('directions.transit_fetch_failed') || 'Failed to fetch route');
            }
            setLocalError(errorMessage);
            setIsLoadingTransit(false);
            // Don't close - show error in popup
        }
    }, [t, activeStop, setState_RouteToStopLocation, setIsLoadingTransit, onClose]);

    // Handle mode selection
    const handleModeSelect = (mode: TravelMode) => {
        setSelectedMode(mode);
        getTransitData(mode);
    };

    if (!open || !latitude || !longitude) {
        // Return null when closed - the modal/page content has its own focusable elements
        return null;
    }

    const destination = `${latitude},${longitude}`;
    const encodedDestination = encodeURIComponent(destination);

    const openInNewTab = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleGoogleMaps = () => {
        setClickingButton('google');
        passiveCollector.trackNavigationUsed('google_maps');
        setTimeout(() => {
            openInNewTab(`https://www.google.com/maps/dir/?api=1&destination=${encodedDestination}`);
            onClose();
            setClickingButton(null);
        }, 200);
    };

    const handleWaze = () => {
        setClickingButton('waze');
        passiveCollector.trackNavigationUsed('waze');
        setTimeout(() => {
            openInNewTab(`https://www.waze.com/ul?ll=${encodedDestination}&navigate=yes`);
            onClose();
            setClickingButton(null);
        }, 200);
    };

    const handleAppleMaps = () => {
        setClickingButton('apple');
        passiveCollector.trackNavigationUsed('apple_maps');
        setTimeout(() => {
            openInNewTab(`http://maps.apple.com/?daddr=${encodedDestination}`);
            onClose();
            setClickingButton(null);
        }, 200);
    };

    // Build array of navigation buttons
    const buttons = [
        // Google Maps
        <Box
            key="google"
            onClick={handleGoogleMaps}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGoogleMaps();
                }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover .icon-circle': {
                    transform: 'scale(1.1)',
                }
            }}
            role="button"
            tabIndex={0}
        >
            <Box
                className="icon-circle"
                sx={{
                    width: 35,
                    height: 35,
                    borderRadius: '50%',
                    // bgcolor: '#4285F4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                    boxShadow: '0 2px 8px rgba(66, 133, 244, 0.3)',
                    transform: clickingButton === 'google' ? 'scale(0.8)' : 'scale(1)',
                }}
            >
                <img src={GoogleMapsIcon} alt="Google Maps" style={{ fontSize: 24, color: 'white', width: 24, height: 24 }} />
            </Box>
        </Box>,

        // Waze
        <Box
            key="waze"
            onClick={handleWaze}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleWaze();
                }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover .icon-circle': {
                    transform: 'scale(1.1)',
                }
            }}
            role="button"
            tabIndex={0}
        >
            <Box
                className="icon-circle"
                sx={{
                    width: 35,
                    height: 35,
                    borderRadius: '50%',
                    bgcolor: '#33CCFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                    boxShadow: '0 2px 8px rgba(51, 204, 255, 0.3)',
                    transform: clickingButton === 'waze' ? 'scale(0.8)' : 'scale(1)',
                }}
            >
                <img src={WazeMapsIcon} alt="Waze" style={{ fontSize: 24, color: 'white', width: 24, height: 24 }} />
            </Box>
        </Box>,

        // Apple Maps (iOS only)
        ...(isIOS ? [
            <Box
                key="apple"
                onClick={handleAppleMaps}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAppleMaps();
                    }
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover .icon-circle': {
                        transform: 'scale(1.1)',
                    }
                }}
                role="button"
                tabIndex={0}
            >
                <Box
                    className="icon-circle"
                    sx={{
                        width: 35,
                        height: 35,
                        borderRadius: '50%',
                        // bgcolor: '#007AFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                        transform: clickingButton === 'apple' ? 'scale(0.8)' : 'scale(1)',
                    }}
                >
                    <img src={AppleMapsIcon} alt="Apple Maps" style={{ fontSize: 24, color: 'white', width: 24, height: 24 }} />
                </Box>
            </Box>
        ] : []),

        // Close Button
        <Box
            key="close"
            onClick={onClose}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClose();
                }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover .icon-circle': {
                    transform: 'scale(1.1)',
                }
            }}
            role="button"
            tabIndex={0}
        >
            <Box
                className="icon-circle"
                sx={{
                    width: 35,
                    height: 35,
                    borderRadius: '50%',
                    background: '#F6F6F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
                    transform: isClosing ? 'scale(0.8)' : 'scale(1)',
                }}
            >
                <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#666' }}>×</span>
            </Box>
        </Box>
    ];

    // Reverse order if specified
    const orderedButtons = reverseOrder ? [...buttons].reverse() : buttons;

    // Desktop list-style navigation row component
    const DesktopNavRow = ({
        icon,
        label,
        onClick,
        showDivider = true,
        isExternal = false
    }: {
        icon: React.ReactNode;
        label: string;
        onClick: () => void;
        showDivider?: boolean;
        isExternal?: boolean;
    }) => (
        <>
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.5,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.04)',
                    },
                    minWidth: 250,
                }}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick();
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {icon}
                    <Typography sx={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#333',
                    }}>
                        {label}
                    </Typography>
                </Box>
                {isExternal && (
                    <i
                        className="fa-regular fa-arrow-up-right-from-square"
                        style={{ fontSize: '14px', color: '#999' }}
                    />
                )}
            </Box>
            {showDivider && <Divider />}
        </>
    );

    // Desktop navigation content (list style like the image)
    const desktopNavigationContent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                py: 0.5,
                minWidth: 250,
            }}
        >
            {/* Travel mode selection — shown directly */}
            <Box sx={{ px: 0.5, py: 1 }}>
                    <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                        {/* Walking */}
                        <Box
                            onClick={() => !isLoadingTransit && handleModeSelect('walking')}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                py: 1,
                                px: 1.5,
                                cursor: isLoadingTransit ? 'default' : 'pointer',
                                borderRadius: '20px',
                                bgcolor: selectedMode === 'walking' ? '#E3F2FD' : '#FFFFFF',
                                border: selectedMode === 'walking' ? '1.5px solid #1976D2' : '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: selectedMode === 'walking' ? '#E3F2FD' : '#F9FAFB' },
                                opacity: isLoadingTransit && selectedMode !== 'walking' ? 0.5 : 1,
                            }}
                        >
                            {isLoadingTransit && selectedMode === 'walking' ? (
                                <CircularProgress size={14} sx={{ color: '#48526B' }} />
                            ) : (
                                <i className="fa-solid fa-person-walking" style={{ fontSize: '12px', color: '#48526B' }} />
                            )}
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#48526B' }}>{t('navigation.mode_walking')}</Typography>
                        </Box>

                        {/* Bicycling */}
                        <Box
                            onClick={() => !isLoadingTransit && handleModeSelect('bicycling')}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                py: 1,
                                px: 1.5,
                                cursor: isLoadingTransit ? 'default' : 'pointer',
                                borderRadius: '20px',
                                bgcolor: selectedMode === 'bicycling' ? '#FFF3E0' : '#FFFFFF',
                                border: selectedMode === 'bicycling' ? '1.5px solid #F57C00' : '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: selectedMode === 'bicycling' ? '#FFF3E0' : '#F9FAFB' },
                                opacity: isLoadingTransit && selectedMode !== 'bicycling' ? 0.5 : 1,
                            }}
                        >
                            {isLoadingTransit && selectedMode === 'bicycling' ? (
                                <CircularProgress size={14} sx={{ color: '#48526B' }} />
                            ) : (
                                <i className="fa-solid fa-bicycle" style={{ fontSize: '12px', color: '#48526B' }} />
                            )}
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#48526B' }}>{t('navigation.mode_bicycling')}</Typography>
                        </Box>

                        {/* Driving */}
                        <Box
                            onClick={() => !isLoadingTransit && handleModeSelect('driving')}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                py: 1,
                                px: 1.5,
                                cursor: isLoadingTransit ? 'default' : 'pointer',
                                borderRadius: '20px',
                                bgcolor: selectedMode === 'driving' ? '#FCE4EC' : '#FFFFFF',
                                border: selectedMode === 'driving' ? '1.5px solid #C2185B' : '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: selectedMode === 'driving' ? '#FCE4EC' : '#F9FAFB' },
                                opacity: isLoadingTransit && selectedMode !== 'driving' ? 0.5 : 1,
                            }}
                        >
                            {isLoadingTransit && selectedMode === 'driving' ? (
                                <CircularProgress size={14} sx={{ color: '#48526B' }} />
                            ) : (
                                <i className="fa-solid fa-car" style={{ fontSize: '12px', color: '#48526B' }} />
                            )}
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#48526B' }}>{t('navigation.mode_driving')}</Typography>
                        </Box>

                        {/* Transit */}
                        <Box
                            onClick={() => !isLoadingTransit && handleModeSelect('transit')}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                py: 1,
                                px: 1.5,
                                cursor: isLoadingTransit ? 'default' : 'pointer',
                                borderRadius: '20px',
                                bgcolor: selectedMode === 'transit' ? '#E8F5E9' : '#FFFFFF',
                                border: selectedMode === 'transit' ? '1.5px solid #388E3C' : '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                '&:hover': { bgcolor: selectedMode === 'transit' ? '#E8F5E9' : '#F9FAFB' },
                                opacity: isLoadingTransit && selectedMode !== 'transit' ? 0.5 : 1,
                            }}
                        >
                            {isLoadingTransit && selectedMode === 'transit' ? (
                                <CircularProgress size={14} sx={{ color: '#48526B' }} />
                            ) : (
                                <i className="fa-solid fa-bus" style={{ fontSize: '12px', color: '#48526B' }} />
                            )}
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#48526B' }}>{t('navigation.mode_transit')}</Typography>
                        </Box>
                    </Box>
                </Box>
            {/* "or" divider line — separates in-app navigation from external apps */}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.75, px: 0.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#E0E0E0' }} />
                <Typography sx={{ mx: 1, color: '#999', fontSize: '14px', fontWeight: 500 }}>
                    {t('navigation.or') || 'or'}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#E0E0E0' }} />
            </Box>

            {/* Other apps — collapsed by default, expands to Waze / Google / Apple icons */}
            {!showOtherApps ? (
                <Box
                    onClick={() => setShowOtherApps(true)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1,
                        px: 0.5,
                        py: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                    role="menuitem"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowOtherApps(true); }
                    }}
                >
                    <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="fa-regular fa-globe" style={{ fontSize: '20px', color: '#48526B' }} />
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#48526B' }}>
                        {t('navigation.other_apps') || 'Other apps'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
                    {/* Waze */}
                    <Box
                        onClick={handleWaze}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            p: 0.5,
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.05)' },
                        }}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleWaze(); } }}
                    >
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: '#33CCFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        }}>
                            <img src={WazeMapsIcon} alt="Waze" style={{ width: 20, height: 20 }} />
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '1px', height: 24, bgcolor: '#E0E0E0', mx: 1.5 }} />

                    {/* Google Maps */}
                    <Box
                        onClick={handleGoogleMaps}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            p: 0.5,
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.05)' },
                        }}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGoogleMaps(); } }}
                    >
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        }}>
                            <img src={GoogleMapsIcon} alt="Google Maps" style={{ width: 36, height: 36 }} />
                        </Box>
                    </Box>

                    {/* Apple Maps (iOS only) */}
                    {isIOS && (
                        <>
                            {/* Divider */}
                            <Box sx={{ width: '1px', height: 24, bgcolor: '#E0E0E0', mx: 1.5 }} />
                            <Box
                                onClick={handleAppleMaps}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    p: 0.5,
                                    transition: 'transform 0.2s ease',
                                    '&:hover': { transform: 'scale(1.05)' },
                                }}
                                role="menuitem"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAppleMaps(); } }}
                            >
                                <Box sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                }}>
                                    <img src={AppleMapsIcon} alt="Apple Maps" style={{ width: 36, height: 36 }} />
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* Error display */}
            {localError && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            px: 0.5,
                            py: 1.5,
                            maxWidth: 250,
                        }}
                    >
                        <Box sx={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa-solid fa-circle-exclamation" style={{ fontSize: '18px', color: '#d32f2f' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                fontSize: '13px',
                                fontWeight: 400,
                                color: '#d32f2f',
                                wordBreak: 'normal',
                                overflowWrap: 'break-word',
                                lineHeight: 1.4,
                            }}>
                                {localError}
                            </Typography>
                            <IOSLocationHelpInline />
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );

    const navigationContent = desktopNavigationContent;

    // Use Popover if enabled
    if (usePopover) {
        // Use offset to adjust popover position
        const offsetTop = offset?.top !== undefined ? offset.top : -8;
        const offsetX = offset?.x !== undefined ? offset.x : 0;

        return (
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                            overflow: 'visible',
                            transform: `translate(${offsetX}px, ${offsetTop}px)`,
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        p: 1,
                        minWidth: 'auto',
                        bgcolor: 'white',
                        borderRadius: '16px',
                    }}
                >
                    {navigationContent}
                </Box>
            </Popover>
        );
    }

    // Handle drag/swipe to close
    const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        startPosRef.current = { x: clientX, y: clientY };
    };

    const handleTouchEnd = (e: React.TouchEvent | React.MouseEvent) => {
        if (!startPosRef.current) return;

        const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'changedTouches' in e ? e.changedTouches[0].clientY : (e as React.MouseEvent).clientY;

        const deltaX = Math.abs(clientX - startPosRef.current.x);
        const deltaY = Math.abs(clientY - startPosRef.current.y);

        // If moved more than 10px, consider it a drag and close
        if (deltaX > 10 || deltaY > 10) {
            onClose();
        }

        startPosRef.current = null;
    };

    // Use fixed Box as default
    // Render outside modal's DOM tree using portal to avoid FocusTrap conflicts

    // Desktop: render as positioned popup
    return createPortal(
        <>
            {/* Invisible overlay to detect clicks/drags outside and close the menu */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: Z_INDEX.modal,
                    backgroundColor: 'transparent',
                }}
                onClick={onClose}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleTouchStart}
                onMouseUp={handleTouchEnd}
            />
            {/* Navigation menu */}
            <Box
                ref={boxRef}
                sx={{
                    position: 'fixed',
                    top: `${calculatedPosition.top}px`,
                    left: `${calculatedPosition.left}px`,
                    transform: calculatedPosition.transform,
                    p: 1,
                    minWidth: 'auto',
                    bgcolor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                    zIndex: Z_INDEX.modal + 1,
                }}
                onClick={(e) => e.stopPropagation()}
                role="menu"
                aria-label="Navigation options"
            >
                {navigationContent}
            </Box>
        </>,
        document.body
    );
};

export default NavigationHelperContent;
